import {
  Panel,
  PanelSection,
  PanelHeader,
  PanelTitle,
  PanelDescription,
  Box,
  Button,
  Flex,
  Text,
  Stack,
  TextLabel,
  VisuallyHidden,
  SegmentedControl,
  Grid,
  TextArea,
  Toggle,
  Heading,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { withForm, Slider } from '@/components/form';
import type { ReactFormExtendedApi } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-form';
import { formOpts } from '../helpers/form';
import { useAnalytics } from '@/services/analytics';
import { useCallback, useEffect, useId, useState, useRef } from 'react';
import { useTransformMessageByBrandVoice } from '../hooks';
import { copyToClipboard } from '@/utils/clipboard';

const SAMPLE_INPUTS = {
  email: [
    "Hi,\n\nWe wanted to reach out with a quick update. Things are moving along as planned, and we'll share more details soon. Let us know if you have any questions.",
    "Hello,\n\nThank you for getting in touch. We've received your request and will follow up within a few business days. We appreciate your patience.",
    'Hi there,\n\nHere’s a brief summary of what we discussed. Next steps are outlined below. Feel free to reply with any feedback or questions.',
    "Hi,\n\nJust a quick note to confirm we're making progress. Everything is on track, and we'll keep you posted with the next update. In the meantime, feel free to reach out with any questions.",
    "Hello,\n\nWe wanted to follow up and let you know that your request is currently being reviewed. We'll share the next steps as soon as they're finalized. Thanks again for your time.",
    "Hi there,\n\nSharing a quick update on our end. We're moving into the next phase and will provide more details shortly. Let us know if there's anything you'd like us to clarify.",
  ],
  sms: [
    "Quick update: we're on it. We'll get back to you soon.",
    "Thanks for your message. We've got what we need and will follow up shortly.",
    'Heads up — here’s the latest. Reply if you have questions.',
    "Update: we're reviewing this now and will be in touch soon.",
    "Got it — thanks for the info. We'll follow up shortly.",
    'Quick heads up: next steps are in motion. More soon.',
  ],
  push: [
    'You have a new update. Tap to see more.',
    "We've got something new for you. Open the app to check it out.",
    'Reminder: don’t forget to take a look when you have a moment.',
    "New activity just in. Open the app to see what's changed.",
    'There’s an update waiting for you. Tap to take a look.',
    'Something’s been updated. Check it out when you’re ready.',
  ],
  'in-app': [
    'Welcome back. Here’s what’s new since your last visit. Take a look when you’re ready.',
    'Your account has been updated. You can review the changes in the section below.',
    'We’ve added something you might find useful. Have a look and tell us what you think.',
    'Thanks for stopping by. Here’s a quick overview of the latest updates.',
    'Good news — we’ve made a few improvements. You can explore them below.',
    'There’s new activity on your account. Review the details in this section.',
  ],
};

type MessageKey = keyof typeof SAMPLE_INPUTS;

interface ToneSliderProps {
  name: string;
  form: ReactFormExtendedApi<any, any, any, any, any, any, any, any, any, any, any, any>;
  label: string;
  description: string;
  leftLabel: string;
  rightLabel: string;
  canEdit?: boolean;
}

function ToneSlider({ name, form, label, description, leftLabel, rightLabel, canEdit = true }: ToneSliderProps) {
  const id = useId();
  const min = 1;
  const max = 5;

  return (
    <Stack gap="050">
      <TextLabel id={`${id}-label`} htmlFor={`${id}-slider`}>
        {label}
      </TextLabel>
      <Stack gap="025">
        <Flex gap="100" alignItems="center" justifyContent="space-between">
          <VisuallyHidden id={`${id}-hidden-label`}>
            Scale is from {min}, {leftLabel} to {max}, {rightLabel}
          </VisuallyHidden>
          <Box flexShrink="0">
            <TextLabel as="span" variant="secondary" whiteSpace="nowrap">
              {leftLabel}
            </TextLabel>
          </Box>
          <Box flexShrink="0">
            <TextLabel as="span" variant="secondary" whiteSpace="nowrap">
              {rightLabel}
            </TextLabel>
          </Box>
        </Flex>
        <Slider
          ariaDescribedby={`${id}-desc ${id}-hidden-label`}
          ariaLabelledby={`${id}-label`}
          name={name}
          api={form}
          minValue={min}
          maxValue={max}
          step={1}
          id={`${id}-slider`}
          isDisabled={!canEdit}
        />
        <Text color="subtle" size="xs" id={`${id}-desc`}>
          {description}
          <VisuallyHidden>.</VisuallyHidden>
        </Text>
      </Stack>
    </Stack>
  );
}

export const ToneInfo = withForm({
  ...formOpts,
  props: {
    canEdit: true,
  },
  render: function ToneInfo({ form, canEdit }) {
    const formData = useStore(form.store, (state) => state.values);

    const [transformedMessage, setTransformedMessage] = useState('');
    const [value, setValue] = useState<MessageKey>('email');
    const [sampleMessage, setSampleMessage] = useState(
      () => SAMPLE_INPUTS[value][Math.floor(Math.random() * SAMPLE_INPUTS[value].length)],
    );
    const snackbars = useSnackbars();

    const [checked, setChecked] = useState(true);

    const { track } = useAnalytics();
    const transformedMessageRef = useRef<HTMLDivElement | null>(null);

    // readonly doesn't work in Pluma TextArea – set it in an effect after the output mounts
    useEffect(() => {
      const el = transformedMessageRef.current;
      if (el != null) {
        const textarea = el.querySelector('textarea');
        if (textarea != null) {
          textarea.readOnly = true;
        }
      }
    }, [transformedMessage]);

    const transformMessageByBrandVoiceMutation = useTransformMessageByBrandVoice({
      message: sampleMessage,
      formData,
      channel: value,
    });

    const handleChannelChange = useCallback(
      (newValue: string) => {
        const key = newValue as MessageKey;
        setValue(key);
        setTransformedMessage('');
        setSampleMessage(SAMPLE_INPUTS[key][Math.floor(Math.random() * SAMPLE_INPUTS[key].length)]);
      },
      [form],
    );

    const isGeneratingOutput = transformMessageByBrandVoiceMutation.isPending;
    const canEditToneSliders = isGeneratingOutput === false ? canEdit : false;

    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>Tone & Voice</PanelTitle>
          <PanelDescription>Adjust settings and apply them to see how they transform sample content</PanelDescription>
        </PanelHeader>
        <PanelSection>
          <Grid columns="1fr 2fr" gap="300">
            <Stack gap="300">
              <Stack gap="025">
                <Heading level="4">Brand Voice</Heading>
                <Text color="subtle">Move sliders to adjust the brand voice</Text>
              </Stack>
              <ToneSlider
                name="formalityScore"
                form={form}
                label="Formality"
                description="How formal or casual your communication style is"
                leftLabel="Casual"
                rightLabel="Formal"
                canEdit={canEditToneSliders}
              />
              <ToneSlider
                name="humorScore"
                form={form}
                label="Humor"
                description="How playful or witty the tone is"
                leftLabel="Serious"
                rightLabel="Humorous"
                canEdit={canEditToneSliders}
              />
              <ToneSlider
                name="respectScore"
                form={form}
                label="Respect"
                description="How cheeky or bold the brand voice is"
                leftLabel="Irreverent"
                rightLabel="Very Respectful"
                canEdit={canEditToneSliders}
              />
              <ToneSlider
                name="energyScore"
                form={form}
                label="Energy"
                description="Overall vibe and enthusiasm level"
                leftLabel="Calm & Direct"
                rightLabel="Enthusiastic"
                canEdit={canEditToneSliders}
              />
              <Button
                onClick={() => {
                  const sampleCustomized = SAMPLE_INPUTS[value].includes(sampleMessage.trim()) === false;
                  void track('aiProfile', 'tonePreviewGetPreviewClicked', {
                    Channel: value,
                    'Sample Input Customized': sampleCustomized,
                  });
                  transformMessageByBrandVoiceMutation.mutate(undefined, {
                    onSuccess: (message) => setTransformedMessage(message),
                  });
                }}
                isDisabled={canEdit === false || sampleMessage === ''}
                icon="sparkle"
                variant="primary"
                isLoading={isGeneratingOutput}
              >
                Get preview
              </Button>
            </Stack>
            <Stack gap="200">
              <Flex justifyContent="space-between" alignItems="start">
                <Heading level="4">Preview</Heading>
                <Toggle
                  label="Show Sample Input"
                  size="sm"
                  isChecked={checked}
                  onChange={(e) => {
                    setChecked(e.currentTarget.checked);
                    void track('aiProfile', 'tonePreviewShowSampleInputToggled', {});
                  }}
                />
              </Flex>
              <SegmentedControl
                label="Message channel"
                name="message_channel"
                value={value}
                onChange={handleChannelChange}
                isDisabled={canEdit === false || isGeneratingOutput}
                options={[
                  { value: 'email', label: 'Email' },
                  { value: 'sms', label: 'SMS' },
                  { value: 'push', label: 'Push' },
                  { value: 'in-app', label: 'In-App' },
                ]}
              />
              {checked ? (
                <TextArea
                  value={sampleMessage}
                  onInput={(e) => setSampleMessage(e.target.value)}
                  label="Sample input"
                  placeholder="Enter a message to see how your brand voice affects it"
                  minRows={4}
                  isDisabled={canEdit === false || isGeneratingOutput}
                />
              ) : null}
              <TextArea label="Output" minRows={4} value={transformedMessage} ref={transformedMessageRef} />
              <Flex justifyContent="space-between" alignItems="center">
                <Flex gap="050">
                  <Button
                    variant="tertiary"
                    onClick={() => {
                      void track('aiProfile', 'tonePreviewShuffleSampleInputClicked', {});
                      setSampleMessage(SAMPLE_INPUTS[value][Math.floor(Math.random() * SAMPLE_INPUTS[value].length)]);
                    }}
                    icon={value}
                    isDisabled={canEdit === false || isGeneratingOutput}
                  >
                    Shuffle sample input
                  </Button>
                  <Button
                    icon="copy-to-clipboard"
                    variant="tertiary"
                    isDisabled={transformedMessage === ''}
                    onClick={async () => {
                      void track('aiProfile', 'tonePreviewCopyOutputClicked', {});
                      const success = await copyToClipboard(transformedMessage);
                      snackbars.create({
                        message: `${success ? 'Copied' : 'Failed to copy'} transformed message to clipboard`,
                        variant: success ? 'success' : 'error',
                      });
                    }}
                  >
                    Copy output
                  </Button>
                </Flex>
              </Flex>
            </Stack>
          </Grid>
        </PanelSection>
      </Panel>
    );
  },
});
